import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../App';
import axios from 'axios';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import { useTranslation } from '../contexts/LanguageContext';
import { trackEvent, GA_CATEGORIES } from '../utils/analytics';
// Logo - using public folder path for static assets
const logoImage = '/assets/img/logo/freedisposibleemail-logo.png';

function Home() {
  const [loading, setLoading] = useState(false);
  const [customUsername, setCustomUsername] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [showDomainDropdown, setShowDomainDropdown] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Fetch available domains on mount
  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/email/domains`);
      const domains = response.data.domains || [];
      setAvailableDomains(domains);
      if (domains.length > 0) {
        setSelectedDomain(domains[0]);
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
      // Fallback to default domain
      setAvailableDomains(['freedisposableemail.com']);
      setSelectedDomain('freedisposableemail.com');
    }
  };

  const generateEmail = async (isRandom = false) => {
    setLoading(true);
    try {
      const username = isRandom ? null : customUsername.trim();
      
      const response = await axios.post(`${API_URL}/email/generate`, {
        username: username || undefined,
        domain: selectedDomain
      });
      
      const { email } = response.data;
      toast.success(t('home.generateButton'));

      // GA4: Track email generation
      trackEvent({
        category: GA_CATEGORIES.TEMP_EMAIL,
        action: 'Generate Email',
        label: 'Homepage',
      });
      
      // Navigate to inbox after a short delay
      setTimeout(() => {
        navigate(`/inbox/${encodeURIComponent(email)}`);
      }, 500);
    } catch (error) {
      console.error('Error generating email:', error);
      const errorMsg = error.response?.data?.error || 'Failed to generate email';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    if (!customUsername.trim() && !loading) {
      toast.error('Please enter a username');
      return;
    }
    generateEmail(false);
  };

  const handleRandom = () => {
    setCustomUsername('');
    generateEmail(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FreeDisposableEmail.com - Free Temporary Email Service | No Registration Required"
        description="Get instant temporary email addresses with FreeDisposableEmail.com. Free disposable email service, no registration required. Protect your privacy from spam and data breaches. Perfect for quick sign-ups, verifications, and testing."
        keywords="temporary email, disposable email, temp mail, throwaway email, fake email, free email, email privacy, spam protection, 10minutemail, gmailnator, trash-mail, temporary email address, disposable email address, free temp mail"
      />
      <Navigation />
      
      {/* Hero Section - Full Width Email Generation Form */}
      <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src={logoImage} 
                alt="FreeDisposableEmail.com Logo"
                className="h-20 lg:h-24 w-auto"
              />
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold mb-2 text-gray-900">
              FreeDisposableEmail.com
            </h1>
            <p className="text-gray-600 text-lg lg:text-xl">
              Free Temporary Email Service - No Registration Required
            </p>
          </div>

          {/* Email Generation Form - Full Width */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 lg:p-8 border border-blue-200/50 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                {/* Username Input */}
                <div className="md:col-span-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('home.customUsername')}
                  </label>
                  <input
                    type="text"
                    value={customUsername}
                    onChange={(e) => setCustomUsername(e.target.value)}
                    placeholder="your-username"
                    className="w-full bg-white text-gray-900 placeholder-gray-400 px-4 py-3.5 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all shadow-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && customUsername.trim()) {
                        handleCreate();
                      }
                    }}
                  />
                </div>

                {/* Domain Selector */}
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('home.selectDomain')}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDomainDropdown(!showDomainDropdown)}
                      className="w-full bg-white text-gray-900 px-4 py-3.5 rounded-xl border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all flex items-center justify-between hover:border-blue-400 shadow-sm"
                    >
                      <span className="text-sm truncate">{selectedDomain || t('home.selectDomain')}</span>
                      <svg className={`w-5 h-5 transition-transform flex-shrink-0 text-gray-500 ${showDomainDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {showDomainDropdown && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setShowDomainDropdown(false)}
                        ></div>
                        <div className="absolute z-20 w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-auto">
                          {availableDomains.map((domain) => (
                            <button
                              key={domain}
                              type="button"
                              onClick={() => {
                                setSelectedDomain(domain);
                                setShowDomainDropdown(false);
                              }}
                              className={`w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors ${
                                selectedDomain === domain ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-700'
                              }`}
                            >
                              {domain}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Generate Button */}
                <div className="md:col-span-3 flex items-end">
                  <button
                    onClick={handleCreate}
                    disabled={loading || !customUsername.trim()}
                    className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white hover:from-blue-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none flex items-center justify-center"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('home.generating')}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('home.generateButton')}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Random Button Row */}
              <div className="flex items-center gap-4 my-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-gray-500 text-sm font-medium">or</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
              
              <button
                onClick={handleRandom}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-emerald-500 text-white hover:from-blue-600 hover:to-emerald-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed font-semibold py-3.5 px-6 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('home.generating')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('home.generateRandomButton')}
                  </>
                )}
              </button>

              {/* Preview */}
              {customUsername && selectedDomain && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Preview:</p>
                  <p className="text-gray-900 font-mono text-lg break-all">
                    {customUsername}@{selectedDomain}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Main Heading */}
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            FreeDisposableEmail.com - Free Temporary Email Service
          </h1>

          {/* Content Sections */}
          <div className="space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                <strong>FreeDisposableEmail.com</strong> is a free temporary email service, also known by various names such as 
                <strong> temp mail</strong>, <strong>10minutemail</strong>, <strong>throwaway email</strong>, 
                <strong> temporary email</strong>, <strong>fake mail</strong>, <strong>gmailnator</strong>, or 
                <strong> trash-mail</strong>.
              </p>

              <p>
                Many websites require you to register before you can access their content. This often means 
                sharing your personal email address, which can lead to unwanted spam and privacy concerns. 
                FreeDisposableEmail.com provides an advanced throwaway disposable email service that helps protect your 
                online privacy.
              </p>

              <p>
                Our service automatically removes trackers and viruses with advanced spam filters, ensuring 
                a clean and secure email experience. Whether you need a temporary email for <strong>Outlook</strong>, 
                <strong> Facebook</strong>, <strong>Twitter</strong>, <strong>Telegram</strong>, <strong>Steam</strong>, 
                <strong> TikTok</strong>, or <strong>Discord</strong>, FreeDisposableEmail.com has you covered.
              </p>

              <p>
                FreeDisposableEmail.com is designed with a responsive layout that works seamlessly on all devices, including 
                Android smartphones and tablets. For even more convenience, check out our 
                <strong> Browser Extensions</strong> that allow you to generate temporary emails with just one click, 
                right from your browser toolbar.
              </p>

              {/* Features Grid */}
              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="text-3xl mb-3">⚡</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Instant Access</h3>
                  <p className="text-sm text-gray-600">
                    Get your temporary email address instantly without any registration required.
                  </p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="text-3xl mb-3">🔒</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Protected</h3>
                  <p className="text-sm text-gray-600">
                    Your emails are private, secure, and automatically deleted after expiry.
                  </p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="text-3xl mb-3">🛡️</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Spam Filtered</h3>
                  <p className="text-sm text-gray-600">
                    Advanced filters remove trackers and viruses from incoming emails.
                  </p>
                </div>
              </div>

              {/* Use Cases */}
              <div className="mt-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                    Perfect For
                  </h2>
                  <p className="text-gray-600 text-lg">
                    Discover the many ways you can use temporary emails
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Quick Website Registrations */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                        Quick Website Registrations
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Sign up for websites instantly without exposing your real email address
                      </p>
                    </div>
                  </div>

                  {/* One-time Verifications */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-green-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                        One-time Verifications
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Complete email verifications quickly and securely without long-term commitments
                      </p>
                    </div>
                  </div>

                  {/* Protecting Main Email */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-purple-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                        Protect Your Main Email
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Keep your primary inbox clean and spam-free by using temporary addresses
                      </p>
                    </div>
                  </div>

                  {/* Testing Services */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-yellow-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-amber-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-yellow-600 transition-colors">
                        Testing Services
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Test applications and services without cluttering your main email account
                      </p>
                    </div>
                  </div>

                  {/* Free Trials */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-red-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-rose-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                        Free Trial Sign-ups
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Try premium services risk-free without sharing your personal email address
                      </p>
                    </div>
                  </div>

                  {/* Temporary Communication */}
                  <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-cyan-300">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        Temporary Communication
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Handle short-term communication needs without permanent email commitments
                      </p>
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
